import { useState, forwardRef } from 'react'
import { useTranslation } from '@/utils/useTranslation'
import { trackCustomEvent } from 'gatsby-plugin-google-analytics'
import tw from 'twin.macro'

import Link from '@/components/link-component'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import SearchNavIcon from '@/components/search/nav-icon'
import routes from '@/constants/routes'
import LogoHorizontal from '@/icons/logo-horizontal.svg'
import Close from '@/icons/bwi/close.svg'
import Hamburger from '@/icons/bwi/hamburger.svg'
import { useHasScrolled } from '@/utils/dom'
import colors from '@/constants/colors'
import NavContainer from '@/components/nav-container'
import StaggerList, { StaggerListItem } from '@/components/ui/stagger-list'
import HeaderBanner from '@/components/header-banner'

const mobileLinkStyle = tw`block py-3 px-5 w-full text-right text-white`

const HelpHeader = forwardRef<HTMLDivElement, any>(
  ({ hasLeftLinks = true, hasSearch = true, twRoot = tw`bg-indigoBlue` }, ref) => {
    const { t } = useTranslation()

    const leftLinks = hasLeftLinks
      ? [
          { text: t('Personal'), url: routes.productsPersonal },
          { text: t('Business'), url: routes.productsBusiness },
          { text: t('Download'), url: routes.download },
          { text: t('Pricing'), url: routes.pricingBusiness },
          { text: t('Help'), url: routes.help },
        ]
      : []

    const [topRef, hasScrolled] = useHasScrolled()

    const [isNavOpen, setNavOpen] = useState(false)

    return (
      <>
        <div ref={topRef as never} tw="h-0" />
        <HeaderBanner isHelpSite />
        <header
          ref={ref}
          css={[
            tw`sticky top-0 left-0 z-50 py-1 text-white transition duration-300`,
            hasScrolled && tw`shadow-lg`,
            twRoot,
          ]}
        >
          {/* Mobile Nav */}
          <nav tw="relative max-w-7xl mx-auto px-5 flex justify-between items-center py-2 md:hidden">
            <Link to={routes.index} title="Bitwarden homepage">
              <LogoHorizontal tw="w-40 text-white" />
            </Link>
            <div tw="flex gap-3 items-center">
              <SearchNavIcon />
              <button
                title="Main navigation"
                tw="text-white rounded  w-9 h-9"
                type="button"
                onClick={() => setNavOpen(true)}
              >
                <Hamburger />
              </button>
            </div>

            {/* Mobile Dropdown */}
            <NavContainer
              isDrawerOpen={isNavOpen}
              isDrawerOnly
              position="right"
              css={[twRoot]}
              onRequestClose={() => {
                setNavOpen(false)
              }}
              showOverlay
            >
              <StaggerList tw="list-none py-2" isVisible={isNavOpen}>
                <StaggerListItem tw="flex flex-col items-end">
                  <button
                    title="Close main navigation"
                    tw="text-white w-9 h-9 p-2 mx-3"
                    type="button"
                    onClick={() => setNavOpen(false)}
                  >
                    <Close />
                  </button>
                </StaggerListItem>
                <StaggerListItem>
                  <Link to={routes.login} css={mobileLinkStyle}>
                    {t('Log In')}
                  </Link>
                </StaggerListItem>
                <StaggerListItem>
                  <Link
                    className="mntn"
                    to={routes.getStarted}
                    onClick={() =>
                      trackCustomEvent({ category: 'Homepage', action: 'Go to vault sign up', label: 'Get started' })
                    }
                    css={mobileLinkStyle}
                  >
                    {t('Get started')}
                  </Link>
                </StaggerListItem>
                {leftLinks.map((link, i) => {
                  const { text, url } = link

                  return (
                    <StaggerListItem key={i}>
                      <Link to={url} css={mobileLinkStyle} activeStyle={{ backgroundColor: 'indigoBlue' }}>
                        {text}
                      </Link>
                    </StaggerListItem>
                  )
                })}
                <StaggerListItem>
                  <Link to={routes.contactSales} css={mobileLinkStyle} activeStyle={{ backgroundColor: 'indigoBlue' }}>
                    {t('Business sales')}
                  </Link>
                </StaggerListItem>
              </StaggerList>
            </NavContainer>
          </nav>

          {/* Desktop Nav */}
          <nav tw="hidden max-w-8xl mx-auto py-2 px-5 justify-between gap-4 md:flex">
            <div tw="flex items-center flex-1">
              <Link to={routes.index} title="Bitwarden homepage">
                <LogoHorizontal tw="w-44 text-white" />
              </Link>
              <div tw="mr-2">
                <ul tw="flex gap-5 list-none">
                  {leftLinks.map((link, i) => {
                    const { text, url } = link

                    return (
                      <li key={i}>
                        <Link
                          to={url}
                          tw="block py-3 border-b-2 border-transparent duration-200 hover:text-gray-300"
                          activeStyle={{ borderColor: 'white' }}
                        >
                          {text}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>
              {hasSearch && <SearchNavIcon />}
              <div id="search-portal" tw="lg:px-4 w-full" />
            </div>
            <div tw="flex gap-5 items-center">
              <CtaLink
                to={routes.contactSales}
                tw="hidden md:block text-sm lg:text-base"
                variant={buttonStyles.btnSolid}
                backgroundColor={colors.indigoBlue}
              >
                {t('Business sales')}
              </CtaLink>

              <CtaLink
                to={routes.getStarted}
                tw="hidden md:block text-sm lg:text-base"
                variant={buttonStyles.btnOutlined}
                backgroundColor={colors.indigoBlue}
                onClick={() =>
                  trackCustomEvent({ category: 'Homepage', action: 'Go to vault sign up', label: 'Get started' })
                }
                className="mntn"
              >
                {t('Get started')}
              </CtaLink>
              <CtaLink
                to={routes.login}
                variant={buttonStyles.btnArrow}
                backgroundColor={colors.indigoBlue}
                tw="text-sm lg:text-base"
              >
                {t('Log In')}
              </CtaLink>
            </div>
          </nav>
        </header>
      </>
    )
  }
)

HelpHeader.displayName = 'HelpHeader'
export default HelpHeader
